;; Based on https://github.com/Day8/re-com/blob/master/src/re_com/dropdown.cljs
;; which is licensed under MIT License (https://github.com/Day8/re-com/blob/master/license.txt)
(ns machtfit.dropdown
  (:require [clojure.string :as str]
            [machtfit.core :as machtfit]
            [machtfit.utils :refer [matches-filter]]
            [reagent.core :as r]))

;;  Inspiration: http://alxlit.name/bootstrap-chosen
;;  Alternative: http://silviomoreto.github.io/bootstrap-select

(defn- deref-or-value
  "Takes a value or an atom
  If it's a value, returns it
  If it's a Reagent object that supports IDeref, returns the value inside it by derefing
  "
  [val-or-atom]
  (if (satisfies? IDeref val-or-atom)
    @val-or-atom
    val-or-atom))

;; ----------------------------------------------------------------------------
;; Utilities for vectors of maps containing :id
;; ----------------------------------------------------------------------------

(defn- position-for-id
  "Takes a vector of maps 'v'. Returns the position of the first item in 'v' whose id-fn (default :id) matches 'id'.
   Returns nil if id not found"
  [id v & {:keys [id-fn] :or {id-fn :id}}]
  (let [index-fn (fn [index item] (when (= (id-fn item) id) index))]
    (first (keep-indexed index-fn v))))

(defn- flex-child-style
  "Determines the value for the 'flex' attribute (which has grow, shrink and basis), based on the :size parameter.
   IMPORTANT: The term 'size' means width of the item in the case of flex-direction 'row' OR height of the item in the case of flex-direction 'column'.
   Flex property explanation:
    - grow    Integer ratio (used with other siblings) to determined how a flex item grows it's size if there is extra space to distribute. 0 for no growing.
    - shrink  Integer ratio (used with other siblings) to determined how a flex item shrinks it's size if space needs to be removed. 0 for no shrinking.
    - basis   Initial size (width, actually) of item before any growing or shrinking. Can be any size value, e.g. 60%, 100px, auto
              Note: auto will cause the initial size to be calculated to take up as much space as possible, in conjunction with it's siblings :flex settings.
   Supported values:
    - initial            '0 1 auto'  - Use item's width/height for dimensions (or content dimensions if w/h not specifed). Never grow. Shrink (to min-size) if necessary.
                                       Good for creating boxes with fixed maximum size, but that can shrink to a fixed smaller size (min-width/height) if space becomes tight.
                                       NOTE: When using initial, you should also set a width/height value (depending on flex-direction) to specify it's default size
                                             and an optional min-width/height value to specify the size it can shrink to.
    - auto               '1 1 auto'  - Use item's width/height for dimensions. Grow if necessary. Shrink (to min-size) if necessary.
                                       Good for creating really flexible boxes that will gobble as much available space as they are allowed or shrink as much as they are forced to.
    - none               '0 0 auto'  - Use item's width/height for dimensions (or content dimensions if not specifed). Never grow. Never shrink.
                                       Good for creating rigid boxes that stick to their width/height if specified, otherwise their content size.
    - 100px              '0 0 100px' - Non flexible 100px size (in the flex direction) box.
                                       Good for fixed headers/footers and side bars of an exact size.
    - 60%                '60 1 0px'  - Set the item's size (it's width/height depending on flex-direction) to be 60% of the parent container's width/height.
                                       NOTE: If you use this, then all siblings with percentage values must add up to 100%.
    - 60                 '60 1 0px'  - Same as percentage above.
    - grow shrink basis  'grow shrink basis' - If none of the above common valaues above meet your needs, this gives you precise control.
   If number of words is not 1 or 3, an exception is thrown.
   Reference: http://www.w3.org/TR/css3-flexbox/#flexibility
   Diagram:   http://www.w3.org/TR/css3-flexbox/#flex-container
   Regex101 testing: ^(initial|auto|none)|(\\d+)(px|%|em)|(\\d+)\\w(\\d+)\\w(.*) - remove double backslashes"
  [size]
  ;; TODO: Could make initial/auto/none into keywords???
  (let [split-size (str/split (str/trim size) #"\s+") ;; Split into words separated by whitespace
        split-count (count split-size)
        _ (assert (contains? #{1 3} split-count) "Must pass either 1 or 3 words to flex-child-style")
        size-only (when (= split-count 1) (first split-size)) ;; Contains value when only one word passed (e.g. auto, 60px)
        split-size-only (when size-only (str/split size-only #"(\d+)(.*)")) ;; Split into number + string
        [_ num units] (when size-only split-size-only) ;; grab number and units
        pass-through? (nil? num) ;; If we can't split, then we'll pass this straign through
        grow-ratio? (or (= units "%") (= units "") (nil? units)) ;; Determine case for using grow ratio
        grow (if grow-ratio? num "0") ;; Set grow based on percent or integer, otherwise no grow
        shrink (if grow-ratio? "1" "0") ;; If grow set, then set shrink to even shrinkage as well
        basis (if grow-ratio? "0px" size) ;; If grow set, then even growing, otherwise set basis size to the passed in size (e.g. 100px, 5em)
        flex (if (and size-only (not pass-through?))
               (str grow " " shrink " " basis)
               size)]
    {:-webkit-flex flex
     :flex flex}))

(defn- align-style
  "Determines the value for the flex align type attributes.
   This parameter determines how children are aligned on the cross axis.
   The justify parameter is a keyword.
   Reference: http://www.w3.org/TR/css3-flexbox/#align-items-property"
  [attribute align]
  (let [attribute-wk (->> attribute name (str "-webkit-") keyword)
        as (case align
             :start "flex-start"
             :end "flex-end"
             :center "center"
             :baseline "baseline"
             :stretch "stretch")]
    {attribute-wk as
     attribute as}))

(defn- move-to-new-choice
  "In a vector of maps (where each map has an :id), return the id of the choice offset posititions away
   from id (usually +1 or -1 to go to next/previous). Also accepts :start and :end"
  [choices id-fn id offset]
  (let [current-index (position-for-id id choices :id-fn id-fn)
        new-index (cond
                    (= offset :start) 0
                    (= offset :end) (dec (count choices))
                    (nil? current-index) 0
                    :else (mod (+ current-index offset) (count choices)))]
    (when (and new-index (pos? (count choices)))
      (id-fn (nth choices new-index)))))

(defn- choices-with-group-headings
  "If necessary, inserts group headings entries into the choices"
  [opts group-fn]
  (let [groups (partition-by group-fn opts)
        group-headers (->> groups
                           (map first)
                           (map group-fn)
                           (map #(hash-map :id (gensym) :group %)))]
    [group-headers groups]))

(defn- filter-choices
  "Filter a list of choices based on a filter string using plain string searches (case insensitive). Less powerful
   than regex's but no confusion with reserved characters"
  [choices group-fn label-fn filter-text]
  (when-not (str/blank? filter-text)
    (filter (fn [opt]
              (let [group (if (nil? (group-fn opt)) "" (group-fn opt))
                    label (str (label-fn opt))]
                (or
                 (matches-filter group filter-text)
                 (matches-filter label filter-text))))
            choices)))

(defn- filter-choices-regex
  "Filter a list of choices based on a filter string using regex's (case insensitive). More powerful but can cause
   confusion for users entering reserved characters such as [ ] * + . ( ) etc."
  [choices group-fn label-fn filter-text]
  (let [re (try
             (js/RegExp. filter-text "i")
             (catch js/Object _exception nil))
        filter-fn (partial (fn [re opt]
                             (when-not (nil? re)
                               (or (.test re (group-fn opt)) (.test re (label-fn opt)))))
                           re)]
    (filter filter-fn choices)))

(defn- show-selected-item
  [node]
  (let [item-offset-top (.-offsetTop node)
        item-offset-bottom (+ item-offset-top (.-clientHeight node))
        parent (.-parentNode node)
        parent-height (.-clientHeight parent)
        parent-visible-top (.-scrollTop parent)
        parent-visible-bottom (+ parent-visible-top parent-height)
        new-scroll-top (cond
                         (> item-offset-bottom parent-visible-bottom) (max (- item-offset-bottom parent-height) 0)
                         (< item-offset-top parent-visible-top) item-offset-top)]
    (when new-scroll-top (set! (.-scrollTop parent) new-scroll-top))))

(defn- make-group-heading
  "Render a group heading"
  [m]
  ^{:key (:id m)} [:li.group-result
                   (:group m)])

(defn- make-rest-placeholder
  "Render a placeholder showing the number of matching but not shown entries"
  [num]
  ^{:key (:id "rest-placeholder")} [:li.info (str "und " num " weitere")])

(defn- choice-item
  "Render a choice item and set up appropriate mouse events"
  [id _label _on-click internal-model]
  (let [mouse-over? (r/atom false)
        !ref (atom nil)
        ref! (partial reset! !ref)
        show! #(when (= @internal-model id) (show-selected-item @!ref))]
    (r/create-class
     {:component-did-mount show!
      :component-did-update show!
      :display-name "choice-item"
      :reagent-render (fn [id label on-click internal-model]
                        (let [selected (= @internal-model id)
                              class (if selected
                                      "highlighted"
                                      (when @mouse-over? "mouseover"))]
                          [:li
                           {:class (str "active-result group-option " class)
                            :ref ref!
                            :on-mouse-over #(reset! mouse-over? true)
                            :on-mouse-out #(reset! mouse-over? false)
                            :on-mouse-down #(on-click id)}
                           label]))})))

(defn- make-choice-item
  [id-fn render-fn callback internal-model opt]
  (let [id (id-fn opt)
        markup (render-fn opt)]
    ^{:key (str id)} [choice-item id markup callback internal-model]))

(defn- filter-text-box
  "Render a filter text box"
  [_filter-box? _filter-text _key-handler _drop-showing? _set-filter-text]
  (let [!ref (atom nil)
        ref! (partial reset! !ref)
        focus! #(.focus (.-firstChild @!ref))]
    (r/create-class
     {:component-did-mount focus!
      :component-did-update focus!
      :reagent-render (fn [filter-box? filter-text key-handler drop-showing? set-filter-text]
                        [:div.chosen-search {:ref ref!}
                         [:input
                          {:type "text"
                           :auto-complete "off"
                           :style (when-not filter-box? {:position "absolute" ;; When no filter box required, use it but hide it off screen
                                                         :width "0px" ;; The rest of these styles make the textbox invisible
                                                         :padding "0px"
                                                         :border "none"})
                           :value @filter-text
                           :on-change #(set-filter-text (-> % .-target .-value))
                           :on-key-down #(when-not (key-handler %)
                                           (.stopPropagation %)
                                           (.preventDefault %)) ;; When key-handler returns false, preventDefault
                           :on-blur #(reset! drop-showing? false)}]])})))

(defn- dropdown-top
  "Render the top part of the dropdown, with the clickable area and the up/down arrow"
  []
  (let [ignore-click (atom false)]
    (fn
      [internal-model _choices _id-fn label-fn tab-index placeholder dropdown-click key-handler filter-box? drop-showing? title?]
      (let [_ (r/set-state (r/current-component) {:filter-box? filter-box?})
            text (if @internal-model
                   (label-fn @internal-model)
                   placeholder)]
        [:a.chosen-single.chosen-default
         {:href "#" ;; Required to make this anchor appear in the tab order
          :tab-index tab-index
          :on-click (comp #(if @ignore-click
                             (reset! ignore-click false)
                             (dropdown-click))
                          machtfit/stop-event)
          :on-mouse-down (comp #(when @drop-showing?
                                  (reset! ignore-click true))
                               machtfit/stop-event)
          :on-key-down (comp (fn [event]
                               (key-handler event)
                               (when (= (.-which event) 13)
                                 (reset! ignore-click true)))
                             machtfit/stop-event)}
         [:span (when title?
                  {:title text})
          text]
         [:div [:b]]])))) ;; This odd bit of markup produces the visual arrow on the right

(defn- load-choices*
  "Load choices if choices is callback."
  [choices-state choices text regex-filter?]
  (let [id (inc (:id @choices-state))
        callback (fn [{:keys [result error]}]
                   (when (= id (:id @choices-state))
                     (swap! choices-state assoc
                            :loading? false
                            :error error
                            :choices result)))]
    (swap! choices-state assoc
           :loading? true
           :error nil
           :id id
           :timer nil)
    (choices {:filter-text text
              :regex-filter? regex-filter?}
             #(callback {:result %})
             #(callback {:error %}))))

(defn- load-choices
  "Load choices or schedule loading depending on debounce?"
  [choices-state choices debounce-delay text regex-filter? debounce?]
  (when (fn? choices)
    (when-let [timer (:timer @choices-state)]
      (js/clearTimeout timer))
    (if debounce?
      (let [timer (js/setTimeout #(load-choices* choices-state choices text regex-filter?) debounce-delay)]
        (swap! choices-state assoc :timer timer))
      (load-choices* choices-state choices text regex-filter?))))

(defn single-dropdown
  "Render a single dropdown component which emulates the bootstrap-choosen style. Sample choices object:
     [{:id \"AU\" :label \"Australia\"      :group \"Group 1\"}
      {:id \"US\" :label \"United States\"  :group \"Group 1\"}
      {:id \"GB\" :label \"United Kingdom\" :group \"Group 1\"}
      {:id \"AF\" :label \"Afghanistan\"    :group \"Group 2\"}]"
  [& {:keys [choices model regex-filter? debounce-delay]
      :or {debounce-delay 250}}]
  (let [external-model (r/atom (deref-or-value model)) ;; Holds the last known external value of model, to detect external model changes
        internal-model (r/atom @external-model) ;; Create a new atom from the model to be used internally
        drop-showing? (r/atom false)
        filter-text (r/atom "")
        choices-fn? (fn? choices)
        choices-state (r/atom {:loading? choices-fn?
                                        ; loading error
                               :error nil
                               :choices []
                                        ; request id to ignore handling response when new request was already made
                               :id 0
                                        ; to debounce requests
                               :timer nil})
        load-choices-fn (partial load-choices choices-state choices debounce-delay)
        set-filter-text (fn [text {:keys [regex-filter?]} debounce?]
                          (load-choices-fn text regex-filter? debounce?)
                          (reset! filter-text text))]
    (load-choices-fn "" regex-filter? false)
    (fn [& {:keys [choices model on-change id-fn label-fn group-fn render-fn disabled? filter-box? regex-filter? placeholder shown-entries title? width max-height tab-index class style attr]
            :or {id-fn :id label-fn :label group-fn :group}
            :as args}]
      (let [render-fn (or render-fn label-fn)
            choices (if choices-fn? (:choices @choices-state) (deref-or-value choices))
            disabled? (deref-or-value disabled?)
            regex-filter? (deref-or-value regex-filter?)
            latest-ext-model (r/atom (deref-or-value model))
            _ (when (not= @external-model @latest-ext-model) ;; Has model changed externally?
                (reset! external-model @latest-ext-model)
                (reset! internal-model @latest-ext-model))
            changeable? (and on-change (not disabled?))
            callback #(do
                        (reset! internal-model %)
                        (when (and changeable? (not= @internal-model @latest-ext-model))
                          (on-change @internal-model))
                        (swap! drop-showing? not) ;; toggle to allow opening dropdown on Enter key
                        (set-filter-text "" args false))
            cancel #(do
                      (reset! drop-showing? false)
                      (set-filter-text "" args false)
                      (reset! internal-model @external-model))
            dropdown-click #(when-not disabled?
                              (swap! drop-showing? not))
            filtered-choices (if choices-fn?
                               choices
                               (if regex-filter?
                                 (filter-choices-regex choices group-fn label-fn @filter-text)
                                 (filter-choices choices group-fn label-fn @filter-text)))
            shown-choices (take shown-entries filtered-choices)
            total-choices (count filtered-choices)
            rest-choices (- total-choices shown-entries)
            press-enter (fn []
                          (if disabled?
                            (cancel)
                            (callback @internal-model))
                          true)
            press-escape (fn []
                           (cancel)
                           true)
            press-tab (fn []
                        (if disabled?
                          (cancel)
                          (do ;; Was (callback @internal-model) but needed a customised version
                            (when changeable? (on-change @internal-model))
                            (reset! drop-showing? false)
                            (set-filter-text "" args false)))
                        (reset! drop-showing? false)
                        true)
            press-up (fn []
                       (if @drop-showing? ;; Up arrow
                         (reset! internal-model (move-to-new-choice filtered-choices id-fn @internal-model -1))
                         (reset! drop-showing? true))
                       true)
            press-down (fn []
                         (if @drop-showing? ;; Down arrow
                           (reset! internal-model (move-to-new-choice filtered-choices id-fn @internal-model 1))
                           (reset! drop-showing? true))
                         true)
            press-home (fn []
                         (reset! internal-model (move-to-new-choice filtered-choices id-fn @internal-model :start))
                         true)
            press-end (fn []
                        (reset! internal-model (move-to-new-choice filtered-choices id-fn @internal-model :end))
                        true)
            key-handler #(if disabled?
                           false
                           (case (.-which %)
                             13 (press-enter)
                             27 (press-escape)
                             9 (press-tab)
                             38 (press-up)
                             40 (press-down)
                             36 (press-home)
                             35 (press-end)
                             filter-box?))] ;; Use this boolean to allow/prevent the key from being processed by the text box
        [:div
         (merge
          {:class (str "rc-dropdown chosen-container chosen-container-single noselect " (when @drop-showing? "chosen-container-active chosen-with-drop ") class)
           :style (merge (flex-child-style (if width "0 0 auto" "auto"))
                         (align-style :align-self :start)
                         {:width width}
                         style)}
          attr) ;; Prevent user text selection
         [dropdown-top internal-model choices id-fn label-fn tab-index placeholder dropdown-click key-handler filter-box? drop-showing? title?]
         (when (and @drop-showing? (not disabled?))
           [:div.chosen-drop
            [filter-text-box filter-box? filter-text key-handler drop-showing? #(set-filter-text % args true)]
            [:ul.chosen-results
             (when max-height {:style {:max-height max-height}})
             (cond
               (str/blank? @filter-text)
               [:li.info "Bitte geben Sie einen Suchbegriff ein"]
               (and choices-fn? (:loading? @choices-state))
               [:li.loading "Lade..."]
               (and choices-fn? (:error @choices-state))
               [:li.error (:error @choices-state)]
               (-> shown-choices count pos?)
               (let [[group-names group-opt-lists] (choices-with-group-headings shown-choices group-fn)
                     make-a-choice (partial make-choice-item id-fn render-fn callback internal-model)
                     make-choices #(map make-a-choice %1)
                     make-h-then-choices (fn [h opts]
                                           (cons (make-group-heading h)
                                                 (make-choices opts)))
                     has-no-group-names? (nil? (:group (first group-names)))]
                 (concat
                  (if (and (= 1 (count group-opt-lists)) has-no-group-names?)
                    (make-choices (first group-opt-lists)) ;; one group means no headings
                    (apply concat (map make-h-then-choices group-names group-opt-lists)))
                  [(when (> rest-choices 0) (make-rest-placeholder rest-choices))]))
               :else
               [:li.no-results (str "Keine passenden Ergebnisse für \"" @filter-text "\"")])]])]))))
